import { useRef, useEffect, useCallback, useState } from 'react'
import WebViewer, { type WebViewerInstance } from '@pdftron/webviewer'
import { env } from '~/env.ts'
import useReviewQuery from '~/graphql/hooks/useReviewQuery'
import { setDefaultViewerSettings } from '~/utilities/webviewer'
import { headerCustomizations } from '~/utilities/webviewer/customizations'
import { useParams, useNavigate } from 'react-router'
import { useSearchParams } from 'react-router-dom'
import Button from '~/components/ui/Button'
import Loading from '~/components/ui/Loading'
import { toast } from 'sonner'
import { useRunSmartMatchJobMutation } from '~/graphql/hooks/useRunSmartMatchJobMutation'
import { getFirefoxVersion } from '~/utilities/utils'
import { useJobManifestQuery } from '~/graphql/hooks/useJobManifestQuery'
import { useDevCycleClient, useVariableValue } from '@devcycle/react-client-sdk'
import { useStateRef } from '~/hooks/useStateRef'
import { useSmartMatchAnnotations } from './hooks/useSmartMatchAnnotations'
import { useDualScroll } from './hooks/useDualScroll'
import { type Match, type MatchPayload } from '~/types/smartMatch'

const UNMATCHED_PAGE_INDEX = -1

const defaultPageMatches = {
  matches: [],
  originalDocumentId: '',
  revisedDocumentId: '',
  comparisonType: ''
}

export default function SmartMatch() {
  const devCycleClient = useDevCycleClient()
  const isApryseProdViewerEnabled = useVariableValue('apryse-prod-viewer-enabled', false)
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const revisedPageParam = searchParams.get('revised-page')
  const { orgId, projectId, reviewId } = useParams<{
    orgId: string
    projectId: string
    reviewId: string
  }>()
  const viewerDivRef = useRef<HTMLDivElement>(null)
  const webviewerInstanceRef = useRef<WebViewerInstance | null>(null)
  const viewerCleanupRef = useRef<(() => void) | null>(null)

  const { job, loading: isLoadingJob } = useReviewQuery(parseInt(reviewId ?? '0'))

  const { jobManifest, loading: isLoadingJobManifest } = useJobManifestQuery(
    parseInt(projectId ?? '0'),
    parseInt(reviewId ?? '0')
  )

  const [smartMatch, setSmartMatch, smartMatchRef] = useStateRef<Match[]>([])
  const [isLoadingDocuments, setIsLoadingDocuments] = useState(true)
  const lastViewerRef = useRef<'viewer1' | 'viewer2' | null>(null)

  const { runSmartMatchJob, loading: isLoadingRunSmartMatchJob } = useRunSmartMatchJobMutation({
    onError: () => {
      toast.error('Error running smart match job')
    }
  })

  const {
    handleAnnotationAdded,
    handleAnnotationDeleted,
    handleAnnotationEdited,
    makeAnnotationSelectedHandler
  } = useSmartMatchAnnotations({ smartMatchRef, setSmartMatch, lastViewerRef })

  const { setupDualScroll } = useDualScroll({
    jobManifestMatches: jobManifest?.data?.matches
  })

  const onProcessMatchesClick = useCallback(async () => {
    const completeMatches = smartMatch.filter(
      (m) =>
        m.originalPageIndex !== UNMATCHED_PAGE_INDEX &&
        m.revisedPageIndex !== UNMATCHED_PAGE_INDEX
    )
    if (completeMatches.length === 0) {
      toast.error(
        'Add at least one complete match (polygon on original and revised) before processing.'
      )
      return
    }
    const processedSmartMatch = combineMatchesByPage(completeMatches)

    const result = await runSmartMatchJob({
      jobId: parseInt(reviewId!),
      smartMatches: processedSmartMatch
    })

    if (result) {
      toast.success('Smart match job run successfully')
      const isAdmin = !orgId
      const smartMatchBackUrl = !isAdmin
        ? `/org/${orgId}/project/${projectId}/reviews/${reviewId}`
        : '/admin/jobs'
      navigate(smartMatchBackUrl)
    } else {
      toast.error('Error running smart match job')
    }
  }, [smartMatch, runSmartMatchJob, reviewId, orgId, projectId, navigate])

  useEffect(() => {
    if (isLoadingJob || !job) return
    if (isLoadingJobManifest || !jobManifest) return
    if (!devCycleClient.isInitialized) return

    let webviewerKey: string = env.VITE_WEBVIEWER_DEMO_TOKEN ?? ''
    if (isApryseProdViewerEnabled && env.VITE_WEBVIEWER_TOKEN) {
      webviewerKey = env.VITE_WEBVIEWER_TOKEN
    }

    const cleanup = () => {
      viewerCleanupRef.current?.()
      viewerCleanupRef.current = null
      if (webviewerInstanceRef.current && viewerDivRef.current) {
        viewerDivRef.current.innerHTML = ''
        webviewerInstanceRef.current = null
      }
      setSmartMatch([])
      setIsLoadingDocuments(true)
      lastViewerRef.current = null
    }

    if (webviewerInstanceRef.current) {
      cleanup()
    }

    if (!viewerDivRef.current) return

    void WebViewer.Iframe(
      {
        path: '/webviewer-0ad25b9a',
        licenseKey: webviewerKey,
        preloadWorker: 'pdf',
        fullAPI: true,
        ui: 'legacy'
      },
      viewerDivRef.current
    ).then((webviewerInstance) => {
      webviewerInstanceRef.current = webviewerInstance

      const { Core, UI } = webviewerInstance

      const version = getFirefoxVersion()
      if (version && version > 145) {
        console.log('Disabling virtual display mode for Firefox version', version)
        const displayMode = Core.documentViewer.getDisplayModeManager()
        displayMode.disableVirtualDisplayMode()
      }

      UI.addEventListener(UI.Events.MULTI_VIEWER_READY, () => {
        const [originalViewer, revisedViewer] = Core.getDocumentViewers()

        if (version && version > 145) {
          console.log('Disabling virtual display mode for Firefox version', version)
          const displayModeOrg = originalViewer.getDisplayModeManager()
          const displayModeRev = revisedViewer.getDisplayModeManager()
          displayModeOrg.disableVirtualDisplayMode()
          displayModeRev.disableVirtualDisplayMode()
        }

        const annotationManagerOrg = originalViewer.getAnnotationManager()
        const annotationManagerRev = revisedViewer.getAnnotationManager()

        // Hidden toggle button for dual scroll (wired to SmartMatch's "Disable Dual Scroll" button)
        const toggleButton = document.createElement('button')
        toggleButton.id = 'mbueDualScrollButton'
        toggleButton.style.position = 'fixed'
        toggleButton.style.display = 'hidden'
        document.body.appendChild(toggleButton)

        const viewerDocument = UI.iframeWindow.document
        const originalContainer = viewerDocument.querySelector('#DocumentContainer1')
        const revisedContainer = viewerDocument.querySelector('#DocumentContainer2')

        // Hide the native Apryse sync button in favour of our own implementation
        viewerDocument.querySelectorAll('[aria-label="Start Sync"]').forEach((button) => {
          ;(button as HTMLElement).style.visibility = 'hidden'
        })

        viewerCleanupRef.current = setupDualScroll(
          originalViewer,
          revisedViewer,
          originalContainer,
          revisedContainer,
          'mbueDualScrollButton'
        )

        const polygonStyle = { StrokeThickness: 8 }
        originalViewer.getTool(Core.Tools.ToolNames.POLYGON).setStyles(polygonStyle)
        revisedViewer.getTool(Core.Tools.ToolNames.POLYGON).setStyles(polygonStyle)

        void originalViewer.loadDocument(job.downloadUrls.original, { extension: 'pdf' })
        void revisedViewer.loadDocument(job.downloadUrls.revised, { extension: 'pdf' })

        annotationManagerOrg.addEventListener(
          'annotationChanged',
          (
            annotations: Parameters<typeof handleAnnotationAdded>[2],
            action: string,
            options: { imported: boolean }
          ) => {
            if (options.imported) return
            if (action === 'add') handleAnnotationAdded(originalViewer, 'viewer1', annotations)
            if (action === 'modify') handleAnnotationEdited('viewer1', annotations)
            if (action === 'delete')
              handleAnnotationDeleted([originalViewer, revisedViewer], 'viewer1', annotations)
          }
        )

        annotationManagerOrg.addEventListener(
          'annotationSelected',
          makeAnnotationSelectedHandler(webviewerInstance, 'orgPolygonId', 0)
        )

        annotationManagerRev.addEventListener(
          'annotationSelected',
          makeAnnotationSelectedHandler(webviewerInstance, 'revPolygonId', 1)
        )

        annotationManagerRev.addEventListener(
          'annotationChanged',
          (
            annotations: Parameters<typeof handleAnnotationAdded>[2],
            action: string,
            options: { imported: boolean }
          ) => {
            if (options.imported) return
            if (action === 'add') handleAnnotationAdded(revisedViewer, 'viewer2', annotations)
            if (action === 'modify') handleAnnotationEdited('viewer2', annotations)
            if (action === 'delete')
              handleAnnotationDeleted([originalViewer, revisedViewer], 'viewer2', annotations)
          }
        )

        const originalLoadPromise = new Promise<void>((resolve) => {
          originalViewer.addEventListener('documentLoaded', resolve)
        })
        const revisedLoadPromise = new Promise<void>((resolve) => {
          revisedViewer.addEventListener('documentLoaded', resolve)
        })

        Promise.all([originalLoadPromise, revisedLoadPromise])
          .then(() => {
            const originalPageCount = originalViewer.getPageCount()
            headerCustomizations(
              webviewerInstance,
              defaultPageMatches,
              'drawing',
              originalPageCount,
              { isLayerToggleEnabled: false }
            )

            const revisedPageNumber = revisedPageParam
              ? Math.min(
                  Math.max(1, parseInt(revisedPageParam, 10)),
                  revisedViewer.getPageCount()
                )
              : null

            if (revisedPageNumber !== null && !Number.isNaN(revisedPageNumber)) {
              revisedViewer.setCurrentPage(revisedPageNumber, false)
            }

            const iframeDoc = UI.iframeWindow.document
            const headerOriginal = iframeDoc.querySelector('#header1 .file-name')
            const headerRevised = iframeDoc.querySelector('#header2 .file-name')
            if (headerOriginal && headerRevised) {
              headerOriginal.innerHTML = 'Original'
              headerRevised.innerHTML = 'Revised'
            }

            UI.setToolMode(Core.Tools.ToolNames.POLYGON)
          })
          .catch(() => {
            toast.error('Error loading documents')
          })
          .finally(() => {
            setIsLoadingDocuments(false)
          })
      })

      setDefaultViewerSettings(webviewerInstance)
      UI.setLayoutMode(UI.LayoutMode.Continuous)
      UI.enterMultiViewerMode()
    })

    return cleanup
    // revisedPageParam intentionally excluded: we only apply it on initial viewer load
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoadingJob, isLoadingJobManifest, job, jobManifest, reviewId])

  const isLoading = isLoadingJob || isLoadingDocuments || isLoadingRunSmartMatchJob

  const completeMatchesCount = smartMatch.filter(
    (match) =>
      match.originalPageIndex !== UNMATCHED_PAGE_INDEX &&
      match.revisedPageIndex !== UNMATCHED_PAGE_INDEX
  ).length

  const incompleteMatchesCount = smartMatch.length - completeMatchesCount
  const canProcessMatches = completeMatchesCount > 0

  return (
    <>
      {isLoading && <Loading mode="dark" />}
      <div className="h-[calc(100vh-122px)] w-full viewer" ref={viewerDivRef} />

      <div className="min-h-[58px] flex flex-row items-center gap-2 pt-2 mb-2 px-4">
        <div className="text-sm w-4/6 text-wrap flex items-center gap-2">
          <span>Click on </span> <PolygonIcon />{' '}
          <span>
            to draw polygons on the original and revised documents to match the elements.
          </span>
          {completeMatchesCount > 0 && (
            <span className="text-muted-foreground">
              ({completeMatchesCount} match{completeMatchesCount !== 1 ? 'es' : ''})
            </span>
          )}
          {incompleteMatchesCount > 0 && (
            <span className="text-amber-500 text-xs">
              {incompleteMatchesCount} incomplete
            </span>
          )}
        </div>
        <div className="flex gap-2 flex-grow justify-end">
          <Button variant="ghost" onClick={() => navigate(-1)}>
            Cancel
          </Button>
          <Button
            variant="default"
            onClick={() => void onProcessMatchesClick()}
            disabled={!canProcessMatches}
          >
            Process Matches
          </Button>
        </div>
      </div>
    </>
  )
}

const PolygonIcon = () => {
  return (
    <div className="inline-block bg-primary color-white rounded p-0.5">
      <svg
        viewBox="0 0 24 24"
        style={{ color: 'white' }}
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
        width="24"
        height="24"
      >
        <path
          d="M20.7,9.3l-8.1-5.8c-0.4-0.3-0.9-0.3-1.3,0L3.3,9.3c-0.4,0.3-0.6,0.8-0.4,1.2L6,20c0.1,0.4,0.5,0.7,1,0.8h10 c0.5,0,0.9-0.3,1-0.8l3.1-9.5C21.2,10.1,21.1,9.6,20.7,9.3z M16.1,18.5H7.9l-2.6-7.8L12,5.8l6.7,4.9L16.1,18.5z"
          fill="currentColor"
        />
        <polygon
          fill="none"
          stroke="#FFFFFF"
          strokeWidth=".5"
          points="16.1,18.5 7.9,18.5 5.3,10.7 12,5.8 18.7,10.7"
        />
      </svg>
    </div>
  )
}

// --- Pure helpers ---

function combineMatchesByPage(matches: Match[]): MatchPayload[] {
  const combined = new Map<string, MatchPayload>()

  for (const match of matches) {
    const key = `${match.originalPageIndex}-${match.revisedPageIndex}`
    const existing = combined.get(key)

    if (existing) {
      existing.originalPolygons = [...existing.originalPolygons, ...match.originalPolygons]
      existing.revisedPolygons = [...existing.revisedPolygons, ...match.revisedPolygons]
      existing.polygon_scales = [...existing.polygon_scales, match.polygon_scale]
    } else {
      combined.set(key, {
        originalPageIndex: match.originalPageIndex,
        originalPolygons: [...match.originalPolygons],
        revisedPageIndex: match.revisedPageIndex,
        revisedPolygons: [...match.revisedPolygons],
        polygon_scales: [match.polygon_scale]
      })
    }
  }
  return Array.from(combined.values())
}
